import React, { useState, useEffect } from 'react';
import { Switch, Row, Col, Space, Input } from 'antd';
import dayjs from 'dayjs';
import { $t } from '~/i18n.js';

export const DateRangeInput = ({ value, onChange, disabled = false }) => {
  const [isMultiDay, setIsMultiDay] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endDate, setEndDate] = useState('');
  const [endTime, setEndTime] = useState('');

  // Initialize from value prop
  useEffect(() => {
    if (value?.date_from && value?.date_to) {
      const start = dayjs(value.date_from);
      const end = dayjs(value.date_to);
      
      setStartDate(start.format('YYYY-MM-DD'));
      setStartTime(start.format('HH:mm'));
      setEndDate(end.format('YYYY-MM-DD'));
      setEndTime(end.format('HH:mm'));
      
      // Check if it's same day or multi-day
      const isSameDay = start.format('YYYY-MM-DD') === end.format('YYYY-MM-DD');
      setIsMultiDay(!isSameDay);
    }
  }, [value?.date_from, value?.date_to]);

  const notifyChange = (newStartDate, newStartTime, newEndDate, newEndTime) => {
    if (onChange && newStartDate && newStartTime && newEndDate && newEndTime) {
      onChange({
        date_from: `${newStartDate}T${newStartTime}`,
        date_to: `${newEndDate}T${newEndTime}`,
      });
    }
  };

  const handleStartDateChange = (e) => {
    const newStartDate = e.target.value;
    setStartDate(newStartDate);
    
    if (!isMultiDay) {
      // Single day mode: always update end date to match start date
      setEndDate(newStartDate);
      notifyChange(newStartDate, startTime, newStartDate, endTime);
    } else {
      // Multi-day mode: ensure end date is at least the start date
      if (!endDate || endDate < newStartDate) {
        setEndDate(newStartDate);
        notifyChange(newStartDate, startTime, newStartDate, endTime);
      } else {
        notifyChange(newStartDate, startTime, endDate, endTime);
      }
    }
  };

  const handleStartTimeChange = (e) => {
    const newStartTime = e.target.value;
    setStartTime(newStartTime);
    
    const finalEndDate = isMultiDay ? endDate : startDate;
    notifyChange(startDate, newStartTime, finalEndDate, endTime);
  };

  const handleEndTimeChange = (e) => {
    const newEndTime = e.target.value;
    setEndTime(newEndTime);
    
    const finalEndDate = isMultiDay ? endDate : startDate;
    notifyChange(startDate, startTime, finalEndDate, newEndTime);
  };

  const handleEndDateChange = (e) => {
    const newEndDate = e.target.value;
    setEndDate(newEndDate);
    
    notifyChange(startDate, startTime, newEndDate, endTime);
  };

  const handleMultiDayToggle = (checked) => {
    setIsMultiDay(checked);
    
    if (checked) {
      // Switching to multi-day: set end to next day if not set
      if (!endDate || endDate === startDate) {
        const nextDay = dayjs(startDate).add(1, 'day').format('YYYY-MM-DD');
        setEndDate(nextDay);
        notifyChange(startDate, startTime, nextDay, endTime);
      }
    } else {
      // Switching to single day: set end date same as start
      setEndDate(startDate);
      notifyChange(startDate, startTime, startDate, endTime);
    }
  };

  const today = dayjs().format('YYYY-MM-DD');

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="small">
      <Row gutter={[8, 8]} align="middle">
        <Col flex="auto">
          <Input
            type="datetime-local"
            value={startDate && startTime ? `${startDate}T${startTime}` : ''}
            onChange={(e) => {
              const value = e.target.value;
              if (value) {
                const [date, time] = value.split('T');
                setStartDate(date);
                setStartTime(time);
                
                if (!isMultiDay) {
                  // Single day mode: always update end date to match start date
                  setEndDate(date);
                  notifyChange(date, time, date, endTime);
                } else {
                  // Multi-day mode: ensure end date is at least the start date
                  if (!endDate || endDate < date) {
                    setEndDate(date);
                    notifyChange(date, time, date, endTime);
                  } else {
                    notifyChange(date, time, endDate, endTime);
                  }
                }
              }
            }}
            disabled={disabled}
            placeholder={$t('van')}
            style={{ width: '100%' }}
            min={`${today}T00:00`}
          />
        </Col>
      </Row>

      <Row gutter={[8, 8]} align="middle">
        <Col>
          <Switch
            checked={isMultiDay}
            onChange={handleMultiDayToggle}
            disabled={disabled}
          />
        </Col>
        <Col>
          <span>{$t('Meerdere dagen?')}</span>
        </Col>
      </Row>

      {!isMultiDay ? (
        <Row gutter={[8, 8]}>
          <Col flex="auto">
            <Input
              type="time"
              value={endTime}
              onChange={handleEndTimeChange}
              disabled={disabled || !startDate}
              placeholder={$t('tot')}
              style={{ width: '100%' }}
            />
          </Col>
        </Row>
      ) : (
        <Row gutter={[8, 8]}>
          <Col flex="auto">
            <Input
              type="datetime-local"
              value={endDate && endTime ? `${endDate}T${endTime}` : ''}
              onChange={(e) => {
                const value = e.target.value;
                if (value) {
                  const [date, time] = value.split('T');
                  setEndDate(date);
                  setEndTime(time);
                  notifyChange(startDate, startTime, date, time);
                }
              }}
              disabled={disabled || !startDate}
              placeholder={$t('tot')}
              style={{ width: '100%' }}
              min={startDate ? `${startDate}T${startTime}` : ''}
            />
          </Col>
        </Row>
      )}
    </Space>
  );
};
